/*
 * AdminDomain360Drawer — deep oversight on a single tenant's custom domain.
 *
 * Shows:
 *   • Tenant + owner identity
 *   • Cached DB state (auto_subdomain, custom_domain, status, provider, CF id)
 *   • Live Cloudflare hostname + SSL status (force re-pull)
 *   • DNS validation records (ssl_records + ownership_verification)
 *   • Deep-link to Cloudflare dashboard
 *   • Recovery actions: Force re-verify · Recreate hostname · Clear (admin override)
 *   • Audit history: every admin_overrides row touching this domain
 */
import { useEffect, useState } from "react";
import {
  X, Globe, ExternalLink, Loader2, ShieldCheck, AlertTriangle, RefreshCw,
  RotateCcw, Trash2, Copy, ChevronRight, History, Database, Wifi,
} from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";


const STATUS_TONE = {
  active:                  "bg-emerald-100 text-emerald-700 border-emerald-300",
  pending_dns_verification:"bg-amber-100 text-amber-800 border-amber-300",
  pending_ssl:             "bg-sky-100 text-sky-800 border-sky-300",
  failed:                  "bg-rose-100 text-rose-800 border-rose-300",
};


export default function AdminDomain360Drawer({ tenantId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null); // 'reverify' | 'recreate' | 'clear'

  const refresh = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/domains/${tenantId}/360`);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load domain 360");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tenantId]);

  if (!tenantId) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} data-testid="domain-360-backdrop"/>
      <div className="fixed inset-y-0 right-0 w-full max-w-3xl bg-background z-50 overflow-y-auto border-l shadow-2xl" data-testid="domain-360-drawer">
        {loading || !data ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-foreground/40"/>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-foreground/55">
                  <Globe className="w-3 h-3"/>Domain 360
                </div>
                <h2 className="text-xl font-semibold mt-1">{data.tenant.name}</h2>
                <div className="text-[11px] font-mono text-foreground/55 mt-0.5">
                  {data.tenant.slug} · owner {data.tenant.owner?.email || "—"}
                </div>
              </div>
              <button onClick={onClose} className="p-1 hover:bg-foreground/5 rounded">
                <X className="w-4 h-4"/>
              </button>
            </div>

            {/* Status header */}
            <DomainStatusCard domain={data.domain} cfLive={data.cloudflare_live} cfError={data.cloudflare_error}/>

            {/* Recovery actions */}
            <div className="rounded-2xl border bg-background p-4">
              <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-3">Recovery actions</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <ActionTile icon={RefreshCw} label="Force re-verify"
                            hint="Pulls live status from Cloudflare and persists" tone="sky"
                            disabled={!data.domain.custom_domain_cloudflare_id}
                            disabledHint={data.domain.custom_domain_cloudflare_id ? null : "No Cloudflare hostname"}
                            onClick={() => setConfirming("reverify")}
                            testid="domain-action-reverify"/>
                <ActionTile icon={RotateCcw} label="Recreate hostname"
                            hint="Delete + re-create CF hostname (stuck-cert recovery)" tone="amber"
                            disabled={!data.domain.custom_domain}
                            disabledHint={data.domain.custom_domain ? null : "No custom domain set"}
                            onClick={() => setConfirming("recreate")}
                            testid="domain-action-recreate"/>
                <ActionTile icon={ShieldCheck} label="Force mark active"
                            hint="Override cert state-machine (use only if domain resolves correctly externally)" tone="emerald"
                            disabled={!data.domain.custom_domain || data.domain.custom_domain_status === "active"}
                            disabledHint={
                              !data.domain.custom_domain ? "No custom domain set" :
                              data.domain.custom_domain_status === "active" ? "Already active" : null
                            }
                            onClick={() => setConfirming("mark-active")}
                            testid="domain-action-mark-active"/>
                <ActionTile icon={Trash2} label="Clear & reset"
                            hint="Wipe every custom-domain field so seller can retry from scratch" tone="rose"
                            disabled={!data.domain.custom_domain}
                            disabledHint={data.domain.custom_domain ? null : "No domain to clear"}
                            onClick={() => setConfirming("clear")}
                            testid="domain-action-clear"/>
              </div>
            </div>

            {/* Cloudflare deep-link */}
            <CloudflareDeepLinkCard url={data.cloudflare_dashboard_url} cfId={data.domain.custom_domain_cloudflare_id}/>

            {/* Live CF roundtrip + Validation records */}
            <ValidationRecordsCard cfLive={data.cloudflare_live}
                                   storedValidation={data.domain.custom_domain_validation}
                                   cnameTarget={data.domain.custom_domain_cname_target}/>

            {/* Auto-configure forensics — what verqo did on the seller's behalf */}
            {data.domain.auto_configure && (
              <AutoConfigureCard ac={data.domain.auto_configure}/>
            )}

            {/* DB cached state */}
            <DbCachedStateCard domain={data.domain}/>

            {/* History */}
            <AuditHistoryCard history={data.history}/>
          </div>
        )}
      </div>

      <ConfirmDialog
        confirming={confirming}
        tenantId={tenantId}
        onClose={() => setConfirming(null)}
        onDone={() => { refresh(); onChanged?.(); }}
      />
    </>
  );
}


function DomainStatusCard({ domain, cfLive, cfError }) {
  const status = domain.custom_domain_status;
  const tone = STATUS_TONE[status] || "bg-foreground/5 border-border text-foreground/65";
  return (
    <div className="rounded-2xl border bg-background p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-foreground/55">Custom domain</div>
          {domain.custom_domain ? (
            <a href={`https://${domain.custom_domain}`} target="_blank" rel="noreferrer"
               className="text-lg font-mono font-semibold hover:underline flex items-center gap-1.5 mt-0.5">
              {domain.custom_domain}
              <ExternalLink className="w-3.5 h-3.5"/>
            </a>
          ) : (
            <div className="text-base font-mono text-foreground/40 mt-0.5">— not configured</div>
          )}
          {domain.auto_subdomain && (
            <div className="text-[11px] font-mono text-foreground/55 mt-1">
              Fallback subdomain: <a href={`https://${domain.auto_subdomain}`} target="_blank" rel="noreferrer" className="hover:underline">{domain.auto_subdomain}</a>
            </div>
          )}
        </div>
        <div className="text-right">
          {status && (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium ${tone}`} data-testid="domain-status-chip">
              {status === "active" ? <ShieldCheck className="w-3 h-3"/> : status === "failed" ? <AlertTriangle className="w-3 h-3"/> : <Globe className="w-3 h-3"/>}
              {status.replace(/_/g, " ")}
            </span>
          )}
          <div className="text-[10px] text-foreground/55 mt-1">
            Provider: <span className="font-mono">{domain.custom_domain_provider || "—"}</span>
          </div>
        </div>
      </div>
      {cfError && (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-900 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5"/>
          <span><strong>Cloudflare API error:</strong> {cfError}</span>
        </div>
      )}
      {cfLive && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
          <div className="rounded-lg bg-foreground/3 border px-3 py-2">
            <div className="text-[9.5px] uppercase tracking-wider text-foreground/55 flex items-center gap-1"><Wifi className="w-2.5 h-2.5"/>Hostname status (live)</div>
            <div className="font-mono mt-0.5">{cfLive.hostname_status || "—"}</div>
          </div>
          <div className="rounded-lg bg-foreground/3 border px-3 py-2">
            <div className="text-[9.5px] uppercase tracking-wider text-foreground/55 flex items-center gap-1"><Wifi className="w-2.5 h-2.5"/>SSL status (live)</div>
            <div className="font-mono mt-0.5">{cfLive.ssl_status || "—"}</div>
          </div>
        </div>
      )}
    </div>
  );
}


function CloudflareDeepLinkCard({ url, cfId }) {
  if (!url && !cfId) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-[11px] text-amber-900 flex items-center gap-2" data-testid="cf-deeplink-card">
        <AlertTriangle className="w-3.5 h-3.5"/>
        Cloudflare for SaaS not configured · set CLOUDFLARE_API_TOKEN + CLOUDFLARE_ZONE_ID + CLOUDFLARE_ACCOUNT_ID in backend .env to enable deep-links and live cert management.
      </div>
    );
  }
  return (
    <div className="rounded-xl border bg-foreground/3 px-3 py-2.5 flex items-center justify-between" data-testid="cf-deeplink-card">
      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-orange-600"/>
        <div>
          <div className="text-[12px] font-medium">Cloudflare dashboard</div>
          <div className="text-[10px] text-foreground/55 font-mono">{cfId ? `hostname id: ${cfId.slice(0, 20)}…` : "Zone dashboard"}</div>
        </div>
      </div>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer"
           className="inline-flex items-center h-8 px-3 text-[12px] font-medium rounded-md border bg-background hover:bg-foreground/5"
           data-testid="cf-deeplink-btn">
          <ExternalLink className="w-3 h-3 mr-1"/>Open in Cloudflare
        </a>
      ) : (
        <span className="text-[10px] text-foreground/55">URL unavailable</span>
      )}
    </div>
  );
}


function ValidationRecordsCard({ cfLive, storedValidation, cnameTarget }) {
  const validation = cfLive ? {
    ssl_records: cfLive.ssl_validation_records || [],
    ownership:   cfLive.ownership_verification || {},
  } : storedValidation || { ssl_records: [], ownership: {} };

  const sslRecords = validation?.ssl_records || [];
  const ownership = validation?.ownership || {};
  const hasAny = sslRecords.length > 0 || ownership.value;

  return (
    <div className="rounded-2xl border bg-background p-4" data-testid="validation-card">
      <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-3 flex items-center gap-2">
        <Database className="w-3 h-3"/>DNS / SSL validation records
      </h4>

      {cnameTarget && (
        <div className="mb-3 rounded-lg bg-foreground/3 border px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-foreground/55 mb-0.5">CNAME target</div>
          <CopyableMono value={cnameTarget} testid="cname-target"/>
          <div className="text-[10px] text-foreground/55 mt-1">Seller must point their custom domain CNAME → this value.</div>
        </div>
      )}

      {sslRecords.length > 0 ? (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider text-foreground/55 mb-1">SSL validation records ({sslRecords.length})</div>
          {sslRecords.map((rec, i) => (
            <div key={i} className="rounded-lg border bg-foreground/3 p-2 mt-1 space-y-1" data-testid={`ssl-record-${i}`}>
              {rec.txt_name && <RecordRow label="TXT name" value={rec.txt_name}/>}
              {rec.txt_value && <RecordRow label="TXT value" value={rec.txt_value}/>}
              {rec.http_url && <RecordRow label="HTTP URL" value={rec.http_url}/>}
              {rec.http_body && <RecordRow label="HTTP body" value={rec.http_body}/>}
              {rec.cname && <RecordRow label="CNAME" value={rec.cname}/>}
              {rec.cname_target && <RecordRow label="CNAME target" value={rec.cname_target}/>}
            </div>
          ))}
        </div>
      ) : null}

      {ownership.value ? (
        <div className="mb-1">
          <div className="text-[10px] uppercase tracking-wider text-foreground/55 mb-1">Ownership verification</div>
          <div className="rounded-lg border bg-foreground/3 p-2 space-y-1" data-testid="ownership-record">
            {ownership.name && <RecordRow label="Name" value={ownership.name}/>}
            {ownership.value && <RecordRow label="Value" value={ownership.value}/>}
            {ownership.type && <RecordRow label="Type" value={ownership.type}/>}
          </div>
        </div>
      ) : null}

      {!hasAny && (
        <div className="text-[11px] text-foreground/55 italic">
          No validation records stored. Either Cloudflare for SaaS is not configured or no custom domain has been registered yet.
        </div>
      )}
    </div>
  );
}


function RecordRow({ label, value }) {
  return (
    <div className="grid grid-cols-[80px,1fr,auto] gap-2 items-center text-[11px]">
      <span className="text-foreground/55">{label}</span>
      <CopyableMono value={value}/>
    </div>
  );
}


function CopyableMono({ value, testid }) {
  const copy = () => {
    try { navigator.clipboard.writeText(value); toast.success("Copied"); } catch { /* ignore */ }
  };
  return (
    <>
      <span className="font-mono text-[11px] break-all" data-testid={testid}>{value}</span>
      <button onClick={copy} className="text-foreground/40 hover:text-foreground"><Copy className="w-3 h-3"/></button>
    </>
  );
}


function DbCachedStateCard({ domain }) {
  return (
    <div className="rounded-2xl border bg-background p-4" data-testid="db-state-card">
      <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-3 flex items-center gap-2">
        <Database className="w-3 h-3"/>DB cached state
      </h4>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
        <KV k="auto_subdomain"   v={domain.auto_subdomain}/>
        <KV k="custom_domain"    v={domain.custom_domain}/>
        <KV k="status"           v={domain.custom_domain_status}/>
        <KV k="provider"         v={domain.custom_domain_provider}/>
        <KV k="cname_target"     v={domain.custom_domain_cname_target}/>
        <KV k="cloudflare_id"    v={domain.custom_domain_cloudflare_id}/>
      </div>
    </div>
  );
}


function KV({ k, v }) {
  return (
    <>
      <div className="text-foreground/55">{k}</div>
      <div className="font-mono text-foreground/85 break-all">{v || <span className="text-foreground/30">—</span>}</div>
    </>
  );
}


function AuditHistoryCard({ history }) {
  return (
    <div className="rounded-2xl border bg-background p-4" data-testid="history-card">
      <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-3 flex items-center gap-2">
        <History className="w-3 h-3"/>Audit history ({history.length})
      </h4>
      {history.length === 0 ? (
        <div className="text-[11px] text-foreground/55 italic">No domain-related admin actions yet.</div>
      ) : (
        <ul className="divide-y">
          {history.map(h => (
            <li key={h.id} className="py-2 text-[11px]" data-testid={`history-row-${h.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono">{h.action}</span>
                <span className="text-[10px] text-foreground/55">{new Date(h.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</span>
              </div>
              <div className="text-[10px] text-foreground/65 mt-0.5">
                by <span className="font-mono">{h.actor_email}</span>{h.reason && ` · ${h.reason}`}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function ActionTile({ icon: Icon, label, hint, tone, disabled, disabledHint, onClick, testid }) {
  const cls = tone === "sky"     ? "border-sky-200 bg-sky-50/50 text-sky-900 hover:bg-sky-100"
            : tone === "amber"   ? "border-amber-200 bg-amber-50/50 text-amber-900 hover:bg-amber-100"
            : tone === "rose"    ? "border-rose-200 bg-rose-50/50 text-rose-900 hover:bg-rose-100"
            : "bg-background";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
            className={`text-left rounded-xl border p-3 transition-colors ${cls} disabled:opacity-50 disabled:cursor-not-allowed`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5"/>
        <span className="text-[12px] font-semibold">{label}</span>
      </div>
      <div className="text-[10px] opacity-70">{disabled && disabledHint ? disabledHint : hint}</div>
    </button>
  );
}


function AutoConfigureCard({ ac }) {
  if (!ac) return null;
  const ok = ac.dns_success === true;
  return (
    <div className={`rounded-2xl border p-4 ${
      ok ? "border-emerald-500/30 bg-emerald-500/5"
         : "border-rose-500/30 bg-rose-500/5"
    }`} data-testid="domain-auto-configure-card">
      <h4 className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 mb-3">
        Auto-configure history (seller-initiated)
      </h4>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12.5px]">
        <dt className="text-foreground/55">DNS provider</dt>
        <dd className="font-medium">{ac.dns_provider || ac.provider || "—"}</dd>

        <dt className="text-foreground/55">Outcome</dt>
        <dd className={`font-medium ${ok ? "text-emerald-700" : "text-rose-700"}`}>
          {ok ? `✓ DNS ${ac.dns_action || "written"}` : "✕ failed"}
        </dd>

        <dt className="text-foreground/55">DNS record ID</dt>
        <dd className="font-mono text-[11.5px] truncate" title={ac.dns_record_id}>
          {ac.dns_record_id ? `${ac.dns_record_id.slice(0, 16)}…` : "—"}
        </dd>

        <dt className="text-foreground/55">DNS zone</dt>
        <dd className="font-mono text-[11.5px] truncate" title={ac.dns_zone_id}>
          {ac.dns_zone_id ? `${ac.dns_zone_id.slice(0, 16)}…` : "—"}
        </dd>

        <dt className="text-foreground/55">Requested at</dt>
        <dd className="font-mono text-[11.5px]">{ac.requested_at?.slice(0, 19) || "—"}</dd>

        <dt className="text-foreground/55">Completed at</dt>
        <dd className="font-mono text-[11.5px]">{ac.completed_at?.slice(0, 19) || "—"}</dd>

        {ac.dns_error && (
          <>
            <dt className="text-foreground/55 col-span-2 mt-2">Error</dt>
            <dd className="col-span-2 text-[11.5px] text-rose-700 mono break-words bg-rose-500/5 p-2 rounded">
              {ac.dns_error}
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}


function ConfirmDialog({ confirming, tenantId, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setReason(""); }, [confirming]);

  if (!confirming) return null;

  const config = {
    reverify: {
      title: "Force re-verify with Cloudflare",
      description: "Pulls the latest hostname + SSL status from Cloudflare API and persists. Use this when seller has updated DNS but status hasn't refreshed.",
      submit: "Re-verify",
      tone:   "default",
      url:    `/admin/domains/${tenantId}/reverify`,
    },
    recreate: {
      title: "Recreate Cloudflare hostname",
      description: "Deletes the existing CF hostname and creates a fresh one. Last-resort recovery for stuck certs. Seller will need to re-verify their DNS records.",
      submit: "Recreate",
      tone:   "destructive",
      url:    `/admin/domains/${tenantId}/recreate`,
    },
    "mark-active": {
      title: "Force mark domain as active",
      description: "Override Cloudflare's cert state-machine and flip the seller's domain status to 'active'. ONLY use this when the domain externally resolves correctly (visit it yourself first) and seller is blocked. Heavily audit-logged.",
      submit: "Mark active",
      tone:   "default",
      url:    `/admin/domains/${tenantId}/mark-active`,
    },
    clear: {
      title: "Clear & reset custom domain",
      description: "Wipes every custom-domain field on the tenant + deletes the Cloudflare hostname so the seller can retry from scratch. Their auto_subdomain (verqo.com URL) is preserved as a fallback.",
      submit: "Clear & reset",
      tone:   "destructive",
      url:    `/admin/domains/${tenantId}/clear`,
    },
  }[confirming];

  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Reason required (min 3 chars)"); return; }
    setBusy(true);
    try {
      const r = await api.post(config.url, { reason: reason.trim() });
      toast.success(r.data.ok ? "Action applied · audit-logged" : "Action complete");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${config.submit} failed`);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!confirming} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`domain-confirm-${confirming}`}>
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <Label>Reason <span className="text-rose-600">*</span></Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Min 3 characters. Audit-logged."
                    data-testid={`domain-confirm-reason-${confirming}`}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant={config.tone} onClick={submit} disabled={busy || reason.trim().length < 3}
                  data-testid={`domain-confirm-submit-${confirming}`}>
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}
            <ChevronRight className="w-3.5 h-3.5 mr-1"/>{config.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
